import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { api, refreshAccessTokenOnce } from '../utils/api';
import { setFeatureFlags, resetFeatureFlags } from '../utils/featureFlags';
import { toast } from 'sonner';

const AuthContext = createContext();

// Maximum session duration: 8 hours in milliseconds
const MAX_SESSION_DURATION = 8 * 60 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken'));
  const [sessionStart, setSessionStart] = useState(() => {
    const stored = localStorage.getItem('sessionStart');
    return stored ? parseInt(stored, 10) : null;
  });
  
  // Flag to prevent fetchCurrentUser after 2FA verification
  const skipFetchRef = useRef(false);

  // WP-2E: a message the login screen must keep showing after the app has
  // signed someone out on purpose (for example: the account was just locked
  // because the 2FA set-up reminder was cleared three times). A toast would
  // fade before they finished reading it.
  // D-SS4 (17/09/2026): the interceptor in utils/api.js hard-navigates when a
  // session is ended by the server, which throws away React state, so it parks
  // the sentence in localStorage first. Pick it up here on the way in and clear
  // it, so it is shown once and never again.
  const [logoutNotice, setLogoutNotice] = useState(() => {
    try {
      const parked = localStorage.getItem('logoutNotice');
      if (parked) {
        localStorage.removeItem('logoutNotice');
        return parked;
      }
    } catch (_e) { /* private windows and blocked storage must not break login */ }
    return '';
  });

  // Check session expiry every minute
  useEffect(() => {
    if (!token || !sessionStart) return;
    
    const checkSessionExpiry = () => {
      const currentTime = Date.now();
      const sessionAge = currentTime - sessionStart;
      
      if (sessionAge >= MAX_SESSION_DURATION) {
        toast.error('Your session has expired after 8 hours. Please log in again.');
        logout();
        return true;
      }
      
      // Warn user 15 minutes before session expires
      const timeRemaining = MAX_SESSION_DURATION - sessionAge;
      if (timeRemaining <= 15 * 60 * 1000 && timeRemaining > 14 * 60 * 1000) {
        toast.warning('Your session will expire in 15 minutes. Please save your work.');
      }
      
      return false;
    };
    
    // Check immediately
    if (checkSessionExpiry()) return;
    
    // Then check every minute
    const interval = setInterval(checkSessionExpiry, 60 * 1000);
    
    return () => clearInterval(interval);
  }, [token, sessionStart]);

  // Auto-refresh token before expiry
  useEffect(() => {
    if (!token || !refreshToken || !sessionStart) return;
    
    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const tokenExpiry = tokenPayload.exp * 1000;
      const timeUntilExpiry = tokenExpiry - Date.now();
      
      // Refresh token 5 minutes before expiry
      if (timeUntilExpiry > 5 * 60 * 1000) {
        const refreshTimeout = setTimeout(() => {
          refreshAccessToken();
        }, timeUntilExpiry - 5 * 60 * 1000);
        
        return () => clearTimeout(refreshTimeout);
      } else if (timeUntilExpiry > 0) {
        // Token expiring soon, refresh now
        refreshAccessToken();
      }
    } catch (error) {
      console.error('Error parsing token for auto-refresh:', error);
    }
  }, [token, refreshToken, sessionStart]);

  // The backend sends the optional features it has switched on with the user
  // object (WP-GP's customer order grouping switch is the first of them). One
  // place applies them, whichever of the four ways a person signed in, and
  // signing out puts every switch back to off.
  useEffect(() => {
    if (user) {
      setFeatureFlags(user.features);
    } else {
      resetFeatureFlags();
    }
  }, [user]);

  // Initial load - fetch current user if token exists
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      
      if (storedToken && !skipFetchRef.current) {
        api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        try {
          const response = await api.get('/auth/me');
          setUser(response.data);
        } catch (error) {
          console.error('Failed to fetch current user:', error);
          // Clear invalid token
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('sessionStart');
          setToken(null);
          setRefreshToken(null);
          setSessionStart(null);
        }
      }
      
      setLoading(false);
      skipFetchRef.current = false;
    };
    
    initializeAuth();
  }, []);

  const refreshAccessToken = async () => {
    try {
      // D-SEC10 (14/09/2026): shared with the 401 interceptor, so the 60-second
      // session check and a screen full of expired requests cost ONE call to
      // /auth/refresh between them rather than one each. The helper stores both
      // tokens; this only has to catch React up with them.
      // D-SS4 (17/09/2026): read the CURRENT refresh token, not the copy React
      // is holding. Changing your own password or switching your own 2FA off
      // hands the browser a fresh pair (`adoptFreshTokens`) which goes straight
      // into localStorage; the stale one in state is now dead, and posting it
      // would sign the person out five minutes before the old token expired —
      // the exact thing the fresh pair exists to prevent.
      const currentRefresh = (() => {
        try { return localStorage.getItem('refreshToken') || refreshToken; }
        catch (_e) { return refreshToken; }
      })();
      if (currentRefresh && currentRefresh !== refreshToken) setRefreshToken(currentRefresh);
      const access_token = await refreshAccessTokenOnce(currentRefresh);
      const new_refresh_token = localStorage.getItem('refreshToken');
      if (new_refresh_token && new_refresh_token !== refreshToken) {
        setRefreshToken(new_refresh_token);
      }

      setToken(access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return true;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      // D-SEC10: a 429 is "you asked too often", NOT "your session is over".
      // The refresh token is still good, so say wait and leave the person
      // signed in — logging them out here would lose their work and make them
      // type a password and a 2FA code, which is itself limited.
      if (error?.response?.status === 429) {
        toast.error(error.friendlyMessage
          || 'Too many attempts from this computer. Wait a minute, then try again.');
        return false;
      }
      toast.error('Session expired. Please log in again.');
      logout();
      return false;
    }
  };

  const login = async (credentials, { deferComplete = false } = {}) => {
    try {
      const response = await api.post('/auth/login', credentials);

      // Check if 2FA is required
      if (response.data.totp_required) {
        return {
          success: true,
          requires2FA: true,
          sessionToken: response.data.session_token,
          userId: response.data.user_id
        };
      }

      // Normal login (no 2FA)
      const { access_token: accessToken, refresh_token, user: userData } = response.data;

      const finalize = () => {
        const now = Date.now();
        // WP-2E: a fresh login is a fresh visit, so any earlier "Not now" goes.
        clearTwoFactorPromptFlag();
        localStorage.setItem('sessionStart', now.toString());
        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', refresh_token);

        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

        setSessionStart(now);
        setToken(accessToken);
        setRefreshToken(refresh_token);
        setUser(userData);

        toast.success(`Welcome back, ${userData.full_name}!`);
      };

      // deferComplete lets the caller play a success animation before the
      // auth state flips and the login screen unmounts.
      if (deferComplete) {
        return { success: true, complete: finalize };
      }

      finalize();
      return { success: true };
    } catch (error) {
      let message = 'Login failed';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors
        message = detail.map(e => e.msg || e.message).join(', ');
      } else if (detail?.msg) {
        message = detail.msg;
      } else if (typeof error.response?.data?.error === 'string') {
        // The per-computer rate limiter answers with {error: "..."} rather than
        // {detail: "..."} — without this the person only saw "Login failed".
        // Its own wording is developer jargon ("Rate limit exceeded: 5 per 1
        // minute"), so for that case say it the way the login screen says it.
        message = error.response.status === 429
          ? 'Too many login attempts from this computer. Wait a minute, then try again.'
          : error.response.data.error;
      }
      toast.error(message);
      // WP-SS1: the status code goes back too, so the login screen can show a
      // standing notice for a 429 "account locked" instead of a toast that fades.
      return { success: false, error: message, status: error.response?.status };
    }
  };

  const verify2FA = async (sessionToken, code, { deferComplete = false } = {}) => {
    try {
      const response = await api.post('/auth/2fa/verify', {
        session_token: sessionToken,
        code
      });

      const { access_token, refresh_token, user: userData } = response.data;

      if (!access_token || !userData) {
        throw new Error('Invalid response from server');
      }

      const finalize = () => {
        const now = Date.now();
        // WP-2E: a fresh login is a fresh visit, so any earlier "Not now" goes.
        clearTwoFactorPromptFlag();
        localStorage.setItem('sessionStart', now.toString());
        localStorage.setItem('token', access_token);
        localStorage.setItem('refreshToken', refresh_token);

        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        skipFetchRef.current = true;

        setSessionStart(now);
        setToken(access_token);
        setRefreshToken(refresh_token);
        setUser(userData);
        setLoading(false);

        toast.success(`Welcome back, ${userData.full_name}!`);
      };

      // deferComplete lets the caller play a success animation before the
      // auth state flips and the login screen unmounts.
      if (deferComplete) {
        return { success: true, complete: finalize, user: userData };
      }

      finalize();
      return { success: true, user: userData };
    } catch (error) {
      console.error('[verify2FA] Error:', error);
      let message = 'Invalid verification code';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail.map(e => e.msg || e.message).join(', ');
      } else if (detail?.msg) {
        message = detail.msg;
      } else if (error.response?.status === 429) {
        // D-SEC10 (14/09/2026): these routes now allow five tries a minute per
        // computer. The limiter answers with {error: ...}, not {detail: ...},
        // so without this the person would be told their code was wrong when
        // it was never looked at.
        message = error.friendlyMessage
          || 'Too many attempts from this computer. Wait a minute, then try again.';
      }
      toast.error(message);
      // D-SEC12 (17/09/2026): five wrong codes now lock the account for 15
      // minutes, and the login screen shows a 429 as a standing notice rather
      // than a toast that fades while the person is still reading it. The
      // status is what tells the screen which kind of answer this was — the
      // password step has returned it since WP-SS1 for exactly this reason.
      return { success: false, error: message, status: error.response?.status };
    }
  };

  const verifyRecoveryCode = async (sessionToken, code, { deferComplete = false } = {}) => {
    try {
      const response = await api.post('/auth/2fa/verify-recovery', {
        session_token: sessionToken,
        code
      });

      const { access_token, refresh_token, user: userData, remaining_recovery_codes } = response.data;

      const finalize = () => {
        const now = Date.now();
        // WP-2E: a fresh login is a fresh visit, so any earlier "Not now" goes.
        clearTwoFactorPromptFlag();
        localStorage.setItem('sessionStart', now.toString());
        localStorage.setItem('token', access_token);
        localStorage.setItem('refreshToken', refresh_token);

        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        skipFetchRef.current = true;

        setSessionStart(now);
        setToken(access_token);
        setRefreshToken(refresh_token);
        setUser(userData);
        setLoading(false);

        toast.success(`Welcome back, ${userData.full_name}!`);
        if (remaining_recovery_codes <= 3) {
          toast.warning(`You have ${remaining_recovery_codes} recovery codes remaining. Consider regenerating them.`);
        }
      };

      // deferComplete lets the caller play a success animation before the
      // auth state flips and the login screen unmounts.
      if (deferComplete) {
        return { success: true, complete: finalize, remainingCodes: remaining_recovery_codes };
      }

      finalize();
      return { success: true, remainingCodes: remaining_recovery_codes };
    } catch (error) {
      let message = 'Invalid recovery code';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail.map(e => e.msg || e.message).join(', ');
      } else if (detail?.msg) {
        message = detail.msg;
      } else if (error.response?.status === 429) {
        // D-SEC10 (14/09/2026): these routes now allow five tries a minute per
        // computer. The limiter answers with {error: ...}, not {detail: ...},
        // so without this the person would be told their code was wrong when
        // it was never looked at.
        message = error.friendlyMessage
          || 'Too many attempts from this computer. Wait a minute, then try again.';
      }
      toast.error(message);
      // D-SEC12: same as verify2FA above — a wrong recovery code counts into
      // the same account lock, so the screen needs the status code.
      return { success: false, error: message, status: error.response?.status };
    }
  };

  // WP-2E: the "Not now" flag is per VISIT, and a new login is a new visit.
  // sessionStorage survives a sign-out and a page reload, and the desktop app is
  // one window that stays open for days, so without this one "Not now" would
  // silence the prompt for everybody who signs in on that computer afterwards.
  // The string is typed out rather than imported from TwoFactorEnrolPrompt.js
  // because that component imports this file (a real import cycle).
  const clearTwoFactorPromptFlag = () => {
    try {
      sessionStorage.removeItem('twofa_prompt_dismissed');
    } catch (e) {
      // Private browsing or a locked-down browser: nothing to clear.
    }
  };

  // WP-2E: re-read the signed-in person from the server. Used after 2FA set-up
  // finishes, or after a "Not now", so the app knows whether to keep showing
  // the set-up prompt without making the person log in again.
  const refreshUser = async () => {
    const response = await api.get('/auth/me');
    setUser(response.data);
    return response.data;
  };

  // WP-2E: logout takes an optional plain-English reason. When one is given it
  // is parked in logoutNotice, and the login screen shows it as a standing
  // message instead of a toast that disappears.
  const logout = (reason) => {
    if (typeof reason === 'string' && reason.trim()) {
      setLogoutNotice(reason.trim());
    }
    // D-SS4: never let a sentence parked by an earlier forced sign-out survive
    // into a deliberate one.
    try { localStorage.removeItem('logoutNotice'); } catch (_e) { /* ignore */ }
    clearTwoFactorPromptFlag();
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionStart');
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setSessionStart(null);
    delete api.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    verify2FA,
    verifyRecoveryCode,
    refreshAccessToken,
    refreshUser,
    logoutNotice,
    setLogoutNotice,
    isAuthenticated: !!user && !!token,
    hasPermission: (permission) => {
      if (!user) return false;
      // Admin role is the ultimate override — protects against an admin
      // accidentally locking themselves out by emptying their permissions.
      if (user.role === 'admin') return true;
      // Explicit per-user permissions list (set by an admin in Staff &
      // Security → Privileges). When it's a defined array — even an empty
      // one — it is authoritative and overrides the role defaults. Only
      // fall through to role defaults when no explicit list has been set.
      if (Array.isArray(user.permissions)) {
        return user.permissions.includes(permission);
      }
      // Fallback: role-based defaults (existing behaviour for users who
      // haven't had their privileges customised).
      const rolePermissions = {
        manager: ['manage_clients', 'create_orders', 'view_production_board', 'invoice', 'access_payroll', 'manage_payroll', 'view_reports', 'use_calculators', 'use_label_designer'],
        production_manager: ['create_orders', 'view_production_board', 'invoice', 'access_payroll', 'manage_payroll', 'view_reports', 'use_calculators', 'use_label_designer'],
        supervisor: ['create_orders', 'view_production_board', 'access_payroll', 'use_calculators'],
        sales: ['manage_clients', 'create_orders', 'invoice', 'view_reports', 'use_calculators'],
        production_staff: ['view_production_board', 'access_payroll', 'use_calculators', 'use_label_designer'],
        production_team: ['view_production_board', 'access_payroll'],
        employee: ['access_payroll']
      };
      return (rolePermissions[user.role] || []).includes(permission);
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
